import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig as OriginalInternalAxiosRequestConfig
} from 'axios';
import { environments } from '@/utils/helpers';
import { useNavigate } from 'react-router-dom';
import {
  readTokens,
  refreshSession,
  setBranchResetHandler,
  setSessionExpiredHandler
} from './authSession';
import { t } from 'i18next';

interface InternalAxiosRequestConfig
  extends OriginalInternalAxiosRequestConfig {
  _retry?: boolean;
  _replayedWithCurrent?: boolean;
  _skipAuthRefresh?: boolean;
}

export interface RequestOptions {
  /** 401 yanıtında otomatik yenileme yapma (oturum kilidi içindeki istekler için). */
  skipAuthRefresh?: boolean;
  /** İsteği iptal etmek için (ör. kilit içindeki süre sınırı). */
  signal?: AbortSignal;
}

const bearerToken = (header: unknown): string | null =>
  typeof header === 'string' && header.startsWith('Bearer ')
    ? header.slice('Bearer '.length)
    : null;

export const API_ERROR_EVENT = 'api_error';

interface ApiErrorDetail {
  message: string;
  severity: 'error' | 'warning' | 'info' | 'success';
}

/**
 * The backend's own 403 text when it explains the refusal. Nest's generic
 * "Forbidden resource" says nothing, so the caller falls back to the app's
 * message.
 */
// Guards and some services answer in English ("Feature 'x' is not
// available in your current plan", "Branch context required", "Access
// denied to this branch"); users get the Turkish text instead.
const FEATURE_NOT_IN_PLAN = /is not available in your current plan/i;
const ENGLISH_REFUSAL =
  /\b(access|denied|permission|not allowed|invalid|required|you (do not|may not)|forbidden|outside)\b/i;
const TURKISH_LETTERS = /[çğıöşüÇĞİÖŞÜ]/;

/**
 * The backend's reason for a 403, when it gives one the user can read:
 * `null` for the generic "Forbidden resource" (use the generic warning),
 * the Turkish plan message for a feature outside the plan, and the generic
 * warning for other English guard texts.
 */
export const specificForbiddenMessage = (data: unknown): string | null => {
  const message =
    data && typeof data === 'object'
      ? (data as { message?: unknown }).message
      : undefined;
  if (typeof message !== 'string') return null;
  const trimmed = message.trim();
  if (!trimmed || /^forbidden( resource)?$/i.test(trimmed)) return null;
  // `?? text`: before i18n has loaded, `t` gives nothing.
  if (FEATURE_NOT_IN_PLAN.test(trimmed)) {
    return t('feature.notInPlan') ?? 'Bu özellik paketinizde bulunmuyor.';
  }
  if (!TURKISH_LETTERS.test(trimmed) && ENGLISH_REFUSAL.test(trimmed)) {
    return t('no.permission.warning') ?? 'Gerekli izinlere sahip değilsiniz';
  }
  return trimmed;
};

export function showApiError(
  message: string,
  severity: ApiErrorDetail['severity'] = 'error'
) {
  if (typeof window !== 'undefined') {
    const event = new CustomEvent(API_ERROR_EVENT, {
      detail: { message, severity }
    });
    window.dispatchEvent(event);
  }
}

class CustomApiClient {
  private readonly instance: AxiosInstance;
  private navigate: ReturnType<typeof useNavigate> | null = null;

  constructor() {
    this.instance = axios.create({
      baseURL: environments.backendBaseUrl,
      headers: {
        'Content-Type': 'application/json'
      }
    });

    this.setupInterceptors();
  }

  public setAuthHeader(token: string) {
    this.instance.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  }

  public setNavigate(navigate: ReturnType<typeof useNavigate>) {
    this.navigate = navigate;
  }

  private redirectToLogin() {
    if (this.navigate) {
      this.navigate('/auth/login');
    } else {
      window.location.href = '/auth/login';
    }
  }

  private setupInterceptors() {
    setSessionExpiredHandler(() => this.redirectToLogin());
    setBranchResetHandler(() =>
      showApiError(t('branch.reset.after.refresh'), 'warning')
    );

    this.instance.interceptors.request.use(
      async (config: InternalAxiosRequestConfig) => {
        const token = localStorage.getItem('access_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        } else {
          // Çıkıştan sonra bellekte kalan eski anahtar gönderilmesin.
          delete this.instance.defaults.headers.common['Authorization'];
          config.headers.delete('Authorization');
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    this.instance.interceptors.response.use(
      (response: AxiosResponse) => response,
      async (error: any) => {
        if (axios.isCancel(error)) {
          showApiError(t('request.canceled'), 'warning');
          return Promise.reject(error);
        }

        const originalRequest = error.config as InternalAxiosRequestConfig;

        if (
          error.response?.status === 401 &&
          !originalRequest._skipAuthRefresh
        ) {
          const sentToken = bearerToken(originalRequest.headers?.Authorization);
          const { accessToken: currentToken } = readTokens();

          // İstek artık geçerli olmayan bir anahtarla gitmişse (başka bir
          // yenileme bu arada bitti) yeni yenileme başlatmadan güncel
          // anahtarla bir kez yeniden gönder.
          if (
            !originalRequest._replayedWithCurrent &&
            currentToken &&
            currentToken !== sentToken
          ) {
            originalRequest._replayedWithCurrent = true;
            return this.instance(originalRequest);
          }

          if (!originalRequest._retry) {
            originalRequest._retry = true;
            // Tüm 401 yanıtları aynı yenileme sözünü bekler; yalnızca
            // yenilemenin 401 veya 403 ile reddi oturumu kapatır.
            await refreshSession(sentToken);
            return this.instance(originalRequest);
          }
        }

        if (error.response?.status === 403) {
          const message = specificForbiddenMessage(error.response.data);
          const isRead =
            (originalRequest?.method ?? 'get').toLowerCase() === 'get';
          if (!isRead || message) {
            // A write the user started, or a refusal that says why (e.g. a
            // feature missing from the plan): tell the user.
            showApiError(message ?? t('no.permission.warning'), 'warning');
          } else {
            // A background read the role may not make (a widget, a lookup):
            // the page shows its own empty state, so no toast.
            console.warn('403 on', originalRequest?.url);
          }
        } else if (error.response?.status === 404) {
          const errorMessage =
            error.response.data && (error.response.data as any).message
              ? (error.response.data as any).message
              : t('not.found.error');
          showApiError(errorMessage, 'warning');
        } else if (error.response?.status === 500) {
          showApiError(t('server.error'), 'error');
        } else if (error.response?.status === 409) {
          const errorMessage =
            error.response.data && (error.response.data as any).message
              ? (error.response.data as any).message
              : t('conflict.error');
          showApiError(errorMessage, 'warning');
        } else if (error.response?.status === 413) {
          showApiError(t('size.too.large.message'), 'error');
        } else if (error.response?.status === 415) {
          showApiError(t('wrong.file.type.message'), 'error');
        }

        return Promise.reject(error);
      }
    );
  }

  async get<T>(url: string, params = {}): Promise<AxiosResponse<T>> {
    const response: AxiosResponse<T> = await this.instance.get(url, {
      params: params
    });
    return response;
  }

  /**
   * Korumalı bir dosyayı Authorization header'ıyla indirip kaydeder.
   * Düz <a href> navigasyonu JWT taşıyamadığı için export endpoint'leri
   * (ör. /exports/...) bu metodla indirilmeli.
   */
  async downloadFile(url: string, filename: string): Promise<void> {
    const response = await this.instance.get<Blob>(url, {
      responseType: 'blob'
    });
    const objectUrl = window.URL.createObjectURL(response.data);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(objectUrl);
  }

  async post<T>(
    url: string,
    data: Record<string, any> | FormData = {},
    options: RequestOptions = {}
  ): Promise<AxiosResponse<T>> {
    let config: AxiosRequestConfig & { _skipAuthRefresh?: boolean } = {};

    if (data instanceof FormData) {
      config = {
        headers: {
          'Content-Type': undefined
        }
      };
    }
    if (options.skipAuthRefresh) config._skipAuthRefresh = true;
    if (options.signal) config.signal = options.signal;
    const response: AxiosResponse<T> = await this.instance.post(
      url,
      data,
      config
    );
    return response;
  }

  async put<T>(
    url: string,
    data: Record<string, any> = {}
  ): Promise<AxiosResponse<T>> {
    try {
      const response: AxiosResponse<T> = await this.instance.put(url, data);
      return response;
    } catch (error) {
      console.error('Error updating data:', error);
      throw error;
    }
  }

  async patch<T>(
    url: string,
    data: Record<string, any> | FormData = {}
  ): Promise<AxiosResponse<T>> {
    try {
      let config = {};

      if (data instanceof FormData) {
        config = {
          headers: {
            'Content-Type': undefined
          }
        };
      }
      const response: AxiosResponse<T> = await this.instance.patch(
        url,
        data,
        config
      );
      return response;
    } catch (error) {
      console.error('Error patching data:', error);
      throw error;
    }
  }

  async delete<T>(url: string, config?: any): Promise<AxiosResponse<T>> {
    try {
      const response: AxiosResponse<T> = await this.instance.delete(
        url,
        config
      );
      return response;
    } catch (error) {
      console.error('Error deleting data:', error);
      throw error;
    }
  }
}

export const apiClient = new CustomApiClient();
export const publicApiClient = axios.create({
  baseURL: environments.backendBaseUrl,
  headers: {
    'Content-Type': 'application/json'
  }
});
